import { computed, makeAutoObservable, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { ChannelType } from '../../../types/channelTypes';
import webViewStore from '../../stores/webViewStore';
import Box from '../../uikit/Box';
import Frame from '../../uikit/Frame';
import { apiClient } from '../../utils/apiClient';
import { useMobxInstance } from '../../utils/mobxUtil';
import { useCommonNavigate } from '../common/CommonLink';
import { MarkdownEditor, MarkdownEditorRef } from '../common/editor/MarkdownEditor';
import { ChannelProvider, useChannelStore } from './ChannelProvider';
import { PostProvider, usePostStore } from './PostProvider';
import localStorageUtil from '../../utils/localStorageUtil';
import _ from 'lodash';

class ViewStore {
    readonly channelType: ChannelType;
    readonly postHash?: string;

    debouncedSaveDraft: _.DebouncedFunc<
        ({ title, content }: { title: string; content: string }) => void
    >;

    isLoading = false;

    constructor(channelType: ChannelType, postHash?: string) {
        makeObservable(this, {
            isLoading: observable,

            isEditMode: computed,
        });

        this.channelType = channelType;
        this.postHash = postHash;

        this.debouncedSaveDraft = _.debounce(
            ({ title, content }: { title: string; content: string }) => {
                this.saveDraft({ title, content });
            },
            2000,
        );
    }

    get isEditMode() {
        return !!this.postHash;
    }

    async savePost({ title, content }: { title: string; content: string }): Promise<string> {
        const created = await apiClient.post.member.createPost.mutate({
            channelType: this.channelType,
            title,
            content,
        });

        // 동기화
        if (created) {
            if (webViewStore.isWebView) {
                webViewStore.syncUi('postCreated', {
                    channelType: this.channelType,
                });
            } else {
                apiClient.post.public.getPostList.$invalidate({
                    channelType: this.channelType,
                });
            }
        }

        return created?.hash || '';
    }

    async editPost({ title, content }: { title: string; content: string }): Promise<string> {
        if (!this.postHash) {
            return '';
        }

        const updated = await apiClient.post.member.updatePost.mutate({
            postHash: this.postHash,
            title,
            content,
        });

        // 캐시 업데이트 및 동기화
        if (updated) {
            if (webViewStore.isWebView) {
                webViewStore.syncUi('postEdited', {
                    channelType: this.channelType,
                    postHash: this.postHash,
                });
            } else {
                apiClient.post.public.getPostByHash.$setCache(
                    {
                        channelType: this.channelType,
                        postHash: updated.hash,
                    },
                    updated,
                );
            }
        }

        return updated?.hash || '';
    }

    loadDraft(): { title: string; content: string } | null {
        const draft = localStorageUtil.getItem({
            key: 'editorDraft',
            keyParams: {
                channelType: this.channelType,
            },
        });

        if (draft) {
            return draft;
        }

        return null;
    }

    saveDraft({ title, content }: { title: string; content: string }) {
        localStorageUtil.setItem({
            key: 'editorDraft',
            keyParams: {
                channelType: this.channelType,
            },
            value: {
                title,
                content,
            },
        });
    }

    deleteDraft() {
        localStorageUtil.deleteItem({
            key: 'editorDraft',
            keyParams: {
                channelType: this.channelType,
            },
        });
    }
}

const PostEditorContent = observer(() => {
    const { channelType } = useParams();
    const { navigateBack } = useCommonNavigate();
    const [isReady, setIsReady] = useState(false);

    const channelStore = useChannelStore();
    const postStore = usePostStore();

    const viewStore = useMobxInstance(ViewStore, channelType as ChannelType, postStore.postHash);

    const editorRef = useRef<MarkdownEditorRef>(null);

    useEffect(() => {
        if (!channelStore.isLoading && !channelStore.isPermissionLoading && !postStore.isLoading) {
            setIsReady(true);
        }
    }, [channelStore.isLoading, channelStore.isPermissionLoading, postStore.isLoading]);

    useEffect(() => {
        if (!isReady) {
            return;
        }

        if (viewStore.isEditMode) {
            editorRef.current?.setInitialContent({
                title: postStore.post?.title || '',
                content: postStore.post?.content || '',
            });
        } else {
            const draft = viewStore.loadDraft();
            if (draft) {
                editorRef.current?.setInitialContent(draft);
            }
        }
    }, [isReady]);

    if (!isReady) {
        return (
            <Frame>
                <Frame.Title title="불러오는 중..." isLoading={true} />
                <Box isLoading={true}>
                    <div>데이터를 불러오는 중입니다...</div>
                </Box>
            </Frame>
        );
    }

    let errorText = '';
    if (!channelStore.channel) {
        errorText = '채널을 찾을 수 없습니다';
    } else if (!channelStore.canWrite) {
        errorText = '채널에 글을 작성할 권한이 없습니다.';
    } else if (viewStore.isEditMode && !postStore.isAuthor) {
        errorText = '본인이 작성한 글만 수정할 수 있습니다.';
    }

    if (errorText) {
        return (
            <Frame>
                <Frame.Title title="오류가 발생했습니다" />
                <Box>
                    <div>{errorText}</div>
                </Box>
            </Frame>
        );
    }

    return (
        <Frame>
            <Frame.Title
                title={
                    viewStore.isEditMode
                        ? `글 수정 (${channelStore.channel!.name})`
                        : `새 글 작성 (${channelStore.channel!.name})`
                }
            />
            <Box>
                <MarkdownEditor
                    ref={editorRef}
                    useTitle={true}
                    onChange={({ title, content, event }) => {
                        if (viewStore.isEditMode) {
                            return;
                        }

                        if (event.isBlur) {
                            viewStore.debouncedSaveDraft.cancel();
                            viewStore.saveDraft({
                                title,
                                content,
                            });
                        } else {
                            viewStore.debouncedSaveDraft({
                                title,
                                content,
                            });
                        }
                    }}
                    onClickSave={async ({ title, content }) => {
                        if (viewStore.isEditMode) {
                            await viewStore.editPost({
                                title,
                                content,
                            });
                        } else {
                            await viewStore.savePost({
                                title,
                                content,
                            });
                        }

                        viewStore.deleteDraft();

                        navigateBack();
                    }}
                    onClickCancel={() => {
                        navigateBack();
                    }}
                />
            </Box>
        </Frame>
    );
});

const PostEditorPage = observer(() => {
    const { channelType } = useParams();
    const [searchParams] = useSearchParams();
    const postHash = searchParams.get('postHash') || undefined;

    return (
        <ChannelProvider channelType={channelType as ChannelType}>
            <PostProvider channelType={channelType as ChannelType} postHash={postHash!}>
                <PostEditorContent />
            </PostProvider>
        </ChannelProvider>
    );
});

export default PostEditorPage;
